import { useState } from 'react';
import { css } from '@emotion/react';
import {
  ContactListEx,
  DeviceInfo,
} from '@weave/schema-gen-ts/dist/schemas/phone/contacts/contact-lists/contact_lists.pb';
import { ContactDirectoriesQuery } from '@frontend/api-contact-directories';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { theme } from '@frontend/theme';
import {
  EditSimpleIcon,
  ListIcon,
  ModalControlTriggerProps,
  Table,
  TextLink,
  useModalControl,
} from '@frontend/design-system';
import { TrayContainer } from '../side-modals';
import { AssignDevicesToDirectoryModal } from '../side-modals/assign-devices-to-directoy-modal';
import { PreviewDevices } from '../side-modals/preview-devices-modal';

export const ContactDirectoriesTable = ({ onClick: openCreateNewDirectoryModal }: ModalControlTriggerProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'contact-directories' });
  const { modalProps: viewDevicesModalProps, triggerProps: viewDevicesTriggerProps } = useModalControl();
  const { modalProps: assignDevicesModalProps, triggerProps: assignDevicesTriggerProps } = useModalControl();
  const [contactDirectoryId, setContactDirectoryId] = useState('');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId;
  const [name, setName] = useState('');
  const [devices, setDevices] = useState<DeviceInfo[]>([]);

  const { useGetContactDirectories } = ContactDirectoriesQuery.useContactDirectoryQueries({ tenantId });
  const { data: { contactLists } = { contactLists: [] }, isLoading } = useGetContactDirectories();
  const { navigate } = useSettingsNavigate();

  const formatTimestamp = useTimestampFormatter();

  const handleOpenAssignDevices = () => {
    viewDevicesModalProps.onClose();
    assignDevicesTriggerProps.onClick();
  };

  return (
    <>
      <Table
        isLoading={isLoading}
        fullHeight
        hasResponsiveColWidths
        data={contactLists ?? ([] as typeof contactLists)}
        colConfig={[
          {
            Header: t('Contact List Name'),
            id: 'contact-list-name',
            accessor: (cell) => cell.name,
          },
          {
            Header: t('Last Updated'),
            id: 'last-updated',
            accessor: (cell) => cell.modifiedAt,
            cellRenderer: (date) => formatTimestamp(date),
          },
          {
            Header: t('Devices Using List'),
            id: 'devices-list',
            accessor: (cell) => cell,
            cellRenderer: ({ name, devices, id }: ContactListEx) => {
              if (!devices) return '-';
              return (
                <TextLink
                  onClick={() => {
                    setName(name);
                    setDevices(devices);
                    setContactDirectoryId(id);
                    viewDevicesTriggerProps.onClick();
                  }}
                  css={css`
                    cursor: pointer;
                  `}
                >
                  {t('{{count}} Devices', { count: devices.length })}
                </TextLink>
              );
            },
            disableSortBy: true,
          },
        ]}
        emptyStateConfig={{
          type: 'contact_directories',
          header: t('No Contact Directories'),
          description: t('Upload contacts to a new list and assign a contact list to the devices in your office.'),
          action: {
            label: t('Add New List'),
            onClick: openCreateNewDirectoryModal,
            trackingId: 'add-new-contact-list',
          },
        }}
        rowActions={{
          actions: [
            {
              label: t('Edit Contact List'),
              Icon: EditSimpleIcon,
              onClick: ({ id, name }) => {
                if (id)
                  navigate({
                    to: '/phone/contact-directories/:id',
                    params: { id },
                    search: { name },
                  });
              },
              trackingId: 'NWX-RPB-EditList',
            },
            {
              label: t('Assign List to Devices'),
              Icon: ListIcon,
              onClick: ({ name, id }) => {
                setName(name);
                setContactDirectoryId(id);
                assignDevicesTriggerProps.onClick();
              },
              trackingId: 'NWX-RPB-AssignDevices',
            },
          ],
        }}
        wrapperStyle={css`
          margin-top: ${theme.spacing(2)};
        `}
      />
      <TrayContainer modalProps={viewDevicesModalProps}>
        <PreviewDevices
          openAssignDevicesModal={handleOpenAssignDevices}
          name={name}
          devices={devices}
          {...viewDevicesModalProps}
        />
      </TrayContainer>
      <TrayContainer modalProps={assignDevicesModalProps}>
        <AssignDevicesToDirectoryModal
          name={name}
          contactDirectoryId={contactDirectoryId}
          {...assignDevicesModalProps}
        />
      </TrayContainer>
    </>
  );
};
